import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useQueue } from "@/hooks/useQueue";
import { Logo } from "@/components/Logo";
import { AddPatientDialog } from "@/components/AddPatientDialog";
import { PatientDetailDialog } from "@/components/PatientDetailDialog";
import { styleOf, saleRichieste, SALE, STATO_STYLE, todayISO } from "@/lib/clinic";
import {
  ArrowLeft, ChevronLeft, ChevronRight, Plus, CalendarDays, Wifi, WifiOff, Clock, Users,
} from "lucide-react";

const GIORNI = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio",
  "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function griglia(anno, mese) {
  const primo = new Date(anno, mese, 1);
  const offset = (primo.getDay() + 6) % 7; // lunedì = 0
  const start = new Date(anno, mese, 1 - offset);
  return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
}

export default function Calendar() {
  const { patients, connected } = useQueue();
  const oggi = todayISO();
  const [cursore, setCursore] = useState(() => {
    const d = new Date();
    return { anno: d.getFullYear(), mese: d.getMonth() };
  });
  const [selezionato, setSelezionato] = useState(oggi);
  const [addOpen, setAddOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const perGiorno = useMemo(() => {
    const map = new Map();
    patients.forEach((p) => {
      const k = p.data || oggi;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(p);
    });
    map.forEach((lista) =>
      lista.sort((a, b) => (a.orario || "~").localeCompare(b.orario || "~") || (a.nome || "").localeCompare(b.nome || ""))
    );
    return map;
  }, [patients, oggi]);

  const celle = griglia(cursore.anno, cursore.mese);
  const delGiorno = perGiorno.get(selezionato) || [];

  const cambiaMese = (delta) =>
    setCursore(({ anno, mese }) => {
      const d = new Date(anno, mese + delta, 1);
      return { anno: d.getFullYear(), mese: d.getMonth() };
    });

  const vaiOggi = () => {
    const d = new Date();
    setCursore({ anno: d.getFullYear(), mese: d.getMonth() });
    setSelezionato(oggi);
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-slate-50">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200">
          <div className="max-w-[1700px] mx-auto px-5 sm:px-8 py-4 flex flex-wrap items-center gap-4 justify-between">
            <div className="flex items-center gap-4">
              <Logo className="h-8 sm:h-9" />
              <div className="h-9 w-px bg-slate-200" />
              <div>
                <h1 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-slate-900 leading-none">
                  Agenda Visite
                </h1>
                <span className="text-[10px] sm:text-xs uppercase tracking-[0.28em] text-slate-400">
                  Prenotazioni e storico
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                data-testid="websocket-connection-status-badge"
                variant="outline"
                className={`gap-1.5 rounded-full ${connected ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-rose-300 text-rose-700 bg-rose-50"}`}
              >
                {connected ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
                {connected ? "Live" : "Offline"}
              </Badge>
              <Button asChild variant="outline" className="rounded-full" data-testid="calendar-back-link">
                <Link to="/"><ArrowLeft className="w-4 h-4 mr-2" /> Gestione</Link>
              </Button>
              <Button className="rounded-full" onClick={() => setAddOpen(true)} data-testid="calendar-add-appointment-btn">
                <Plus className="w-4 h-4 mr-2" /> Nuova prenotazione
              </Button>
            </div>
          </div>
        </header>

        <main className="max-w-[1700px] mx-auto px-5 sm:px-8 py-7 grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6">
          <section className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" className="rounded-full" onClick={() => cambiaMese(-1)} data-testid="calendar-prev-month">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="font-display text-xl font-bold text-slate-900 min-w-[190px] text-center" data-testid="calendar-month-label">
                  {MESI[cursore.mese]} {cursore.anno}
                </div>
                <Button variant="ghost" size="icon" className="rounded-full" onClick={() => cambiaMese(1)} data-testid="calendar-next-month">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              <Button variant="outline" size="sm" className="rounded-full" onClick={vaiOggi} data-testid="calendar-today-btn">
                <CalendarDays className="w-4 h-4 mr-2" /> Oggi
              </Button>
            </div>

            <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
              {GIORNI.map((g) => (
                <div key={g} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                  {g}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7" data-testid="calendar-grid">
              {celle.map((d) => {
                const k = iso(d);
                const lista = perGiorno.get(k) || [];
                const fuoriMese = d.getMonth() !== cursore.mese;
                const isOggi = k === oggi;
                const attivo = k === selezionato;
                return (
                  <button
                    key={k}
                    onClick={() => setSelezionato(k)}
                    data-testid={`calendar-cell-${k}`}
                    className={`min-h-[104px] border-b border-r border-slate-100 p-2 text-left transition-colors align-top ${
                      fuoriMese ? "bg-slate-50/60" : "hover:bg-sky-50/50"
                    } ${attivo ? "ring-2 ring-inset ring-sky-500 bg-sky-50/60" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={`font-ticket text-sm ${
                          isOggi
                            ? "bg-slate-900 text-white rounded-full w-6 h-6 grid place-items-center"
                            : fuoriMese
                            ? "text-slate-300"
                            : "text-slate-700"
                        }`}
                      >
                        {d.getDate()}
                      </span>
                      {lista.length > 0 && (
                        <span className="text-[10px] font-bold text-slate-400">{lista.length}</span>
                      )}
                    </div>
                    <div className="mt-1.5 space-y-1">
                      {lista.slice(0, 3).map((p) => {
                        const st = styleOf(p);
                        return (
                          <div
                            key={p.id}
                            data-testid="calendar-event-chip"
                            className={`truncate rounded px-1.5 py-0.5 text-[10px] font-semibold ${st.soft}`}
                          >
                            {p.orario ? `${p.orario} ` : ""}
                            {p.nome}
                          </div>
                        );
                      })}
                      {lista.length > 3 && (
                        <div className="text-[10px] text-slate-400 pl-1">+{lista.length - 3} altri</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          <aside className="rounded-xl border border-slate-200 bg-white h-fit" data-testid="calendar-day-panel">
            <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
              <div>
                <div className="font-display text-lg font-bold text-slate-900">
                  {new Date(selezionato).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                  <Users className="w-3.5 h-3.5" /> {delGiorno.length} prenotazioni
                </div>
              </div>
              <Tooltip delayDuration={150}>
                <TooltipTrigger asChild>
                  <Button size="icon" variant="outline" className="rounded-full shrink-0" onClick={() => setAddOpen(true)} data-testid="calendar-day-add-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Aggiungi in questa data</TooltipContent>
              </Tooltip>
            </div>

            <ul className="divide-y divide-slate-100 max-h-[62vh] overflow-y-auto" data-testid="calendar-day-list">
              {delGiorno.map((p) => {
                const st = styleOf(p);
                const sta = STATO_STYLE[p.stato] || STATO_STYLE.da_accettare;
                return (
                  <li key={p.id}>
                    <button
                      onClick={() => setDetail(p)}
                      data-testid="calendar-day-item"
                      className="w-full text-left px-5 py-3 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-2.5">
                        <span className={`font-ticket text-xs font-bold rounded px-1.5 py-0.5 ${st.badge}`}>
                          {p.codice || "—"}
                        </span>
                        <span className="font-medium text-slate-900 text-sm flex-1 truncate">{p.nome}</span>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${sta.cls}`}>
                          {sta.label}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {p.orario || "senza orario"}
                        </span>
                        <span className="truncate max-w-[160px]">{p.azienda || "—"}</span>
                        <span>{saleRichieste(p).map((r) => SALE[r].short).join(" + ")}</span>
                      </div>
                    </button>
                  </li>
                );
              })}
              {delGiorno.length === 0 && (
                <li className="px-5 py-14 text-center text-slate-400 font-display text-lg">
                  Nessuna prenotazione
                </li>
              )}
            </ul>
          </aside>
        </main>

        <AddPatientDialog open={addOpen} onOpenChange={setAddOpen} defaultDate={selezionato} />
        <PatientDetailDialog patient={detail} open={!!detail} onOpenChange={(o) => !o && setDetail(null)} />
      </div>
    </TooltipProvider>
  );
}
